import { StoreAccessBoundary, SessionUnavailable } from "@/shared/access/StoreAccess";
import { useEffect } from "react";
import { LoadingSkeleton } from "@a2z/ui";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { RouteErrorBoundary } from "@/shared/error/RouteErrorBoundary";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useLocale } from "./locale";
import { RestaurantStoreTerminal } from "@/shared/layout/RestaurantStoreTerminal";

/**
 * Authenticated layout for the Quick Setup onboarding wizard. Runs the same
 * session + store-scope checks as `ProtectedLayout`, but skips `AppShell`
 * (sidebar + topbar) so the wizard owns the full viewport — matching the
 * full-screen onboarding design. Unlike `ProtectedLayout` it does NOT apply the
 * "needs onboarding" redirect (this layout IS the onboarding destination).
 */
export function ProtectedOnboardingLayout() {
  const locale = useLocale();
  const { storeSlug = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslations();
  const { data, isLoading, error, refetch } = useSessionQuery({ storeSlug });

  useEffect(() => {
    if (
      error instanceof ApiJsonClientError &&
      (error.status === 401 || error.status === 403)
    ) {
      navigate(`/${locale}/login`, { replace: true });
      return;
    }

    const authenticatedStoreSlug = data?.seller?.storeSlug?.trim();
    if (authenticatedStoreSlug && authenticatedStoreSlug !== storeSlug) {
      navigate(`/${locale}/${authenticatedStoreSlug}/quick-setup`, { replace: true });
    }
  }, [data?.seller?.storeSlug, error, locale, navigate, storeSlug]);

  if (
    error instanceof ApiJsonClientError &&
    (error.status === 401 || error.status === 403)
  ) {
    return <Navigate to={`/${locale}/login`} replace />;
  }

  const authenticatedStoreSlug = data?.seller?.storeSlug?.trim();
  if (authenticatedStoreSlug && authenticatedStoreSlug !== storeSlug) {
    return (
      <Navigate to={`/${locale}/${authenticatedStoreSlug}/quick-setup`} replace />
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[100svh] w-full items-center justify-center bg-[color:var(--background)] p-6">
        <div role="status" aria-busy="true" className="w-full max-w-xl">
          <span className="sr-only">{t(keys.common.loading)}</span>
          <LoadingSkeleton variant="card" rows={3} />
        </div>
      </div>
    );
  }

  if (error || !data) return <SessionUnavailable locale={locale} retry={refetch} />;

  if (data?.mustChangePassword) {
    return <Navigate to={`/${locale}/change-password`} replace />;
  }

  // Decision D13 — a RESTAURANT store is administered only in apps/restaurant.
  // The check lives HERE, at the protected layout, rather than in the nav: a
  // hidden nav still leaves every seller route reachable by URL, so a deep link
  // to /{locale}/{slug}/orders would render the page and the portal would be
  // half-usable — exactly what the decision exists to prevent.
  if (data?.seller?.storeKind === "RESTAURANT") {
    return (
      <RestaurantStoreTerminal
        locale={locale}
        storeSlug={storeSlug}
        availableStores={data.availableStores ?? []}
      />
    );
  }

  return (
    // `100svh`, not `h-screen` (100vh) — same mobile URL-bar bug fixed in
    // ProtectedEditorLayout: on a real phone 100vh is the URL-bar-retracted
    // (large) viewport, so this overflow-hidden non-scrolling wizard shell was
    // ~72px too tall and its bottom-anchored controls landed under the browser
    // URL bar. `svh` pins it to the always-visible area. Desktop unaffected.
    <StoreAccessBoundary session={data} locale={locale} storeSlug={storeSlug}>
    <div key={location.pathname} className="h-[100svh] w-screen overflow-hidden">
      <RouteErrorBoundary>
        <Outlet />
      </RouteErrorBoundary>
    </div>
    </StoreAccessBoundary>
  );
}
